/**
 * #11258 (MST-6670): a Pause that errored or timed out is not proof the streamer
 * is still playing. Bill Greenfield's log: "Pause timed out after 2s" while the
 * Connector was still delivering it — the UI flipped back to "playing", the
 * wall clock kept advancing tracks, and the streamer sat paused and silent.
 *
 * Ask the device before reverting. Paused/stopped → show paused. Playing on
 * every read → revert (the pause really did not land). Unreadable → keep the
 * old contract (#1130: revert to the prior state).
 */
import type { PlaybackState } from '../protocols/BATON';

export const PAUSE_VERIFY_READS = 3;
export const PAUSE_VERIFY_INTERVAL_MS = 600;

export type PauseVerifyVerdict = 'paused' | 'playing' | 'unknown';

export function transportStateFromXml(raw: string): string {
  const m = String(raw || '').match(/<CurrentTransportState>(.*?)<\/CurrentTransportState>/);
  return (m ? m[1] : String(raw || '')).trim().toUpperCase();
}

export function stateMeansNotPlaying(state: string): boolean {
  return state === 'PAUSED_PLAYBACK' || state === 'STOPPED' || state === 'NO_MEDIA_PRESENT';
}

export async function verifyRendererAfterPauseFailure(
  rendererId: string,
  getTransportInfo: (id: string) => Promise<string>,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<PauseVerifyVerdict> {
  let sawPlaying = false;
  for (let i = 0; i < PAUSE_VERIFY_READS; i++) {
    if (i > 0) await sleep(PAUSE_VERIFY_INTERVAL_MS);
    try {
      const state = transportStateFromXml(await getTransportInfo(rendererId));
      if (stateMeansNotPlaying(state)) return 'paused';
      if (state === 'PLAYING') sawPlaying = true;
    } catch {
      /* unreadable this round — try again */
    }
  }
  return sawPlaying ? 'playing' : 'unknown';
}

/** UI state to set after a failed pause, or null to leave it alone. */
export async function pauseFailureUiState(
  rendererId: string | null,
  prior: PlaybackState,
): Promise<PlaybackState | null> {
  let verdict: PauseVerifyVerdict = 'unknown';
  if (rendererId) {
    try {
      const C = await import('../lib/connector');
      verdict = await verifyRendererAfterPauseFailure(rendererId, (id) => C.getTransportInfo(id));
    } catch {
      verdict = 'unknown';
    }
  }
  if (verdict === 'paused') return 'paused';
  return prior === 'playing' || prior === 'loading' ? prior : null;
}
